import { projectForReaders } from './connectedBible'
import { slugify } from './model'
import { downloadBlob } from './download'
import type { StoryProject } from './types'
import { describeCondition, describeEffect, displayValue } from './storyLogic'

export function manuscriptText(project: StoryProject, markdown = false) {
  project = projectForReaders(project)
  const title = project.title || 'Untitled story'
  const lines = [
    markdown ? '# ' + title : title.toUpperCase(),
    '',
    'Screenplay draft · ' + project.nodes.length + ' scenes',
    '',
  ]
  const destination = (id: string | null) =>
    project.nodes.find((node) => node.id === id)?.title || '[unconnected]'
  const variables = project.variables ?? []
  if (variables.length) {
    lines.push(
      'STORY STARTING VALUES',
      '',
      ...variables.map(
        (variable) =>
          variable.name + ': ' + displayValue(variable.initialValue),
      ),
      '',
    )
  }
  project.nodes.forEach((node, index) => {
    lines.push(
      markdown ? '---' : '────────────────────────────────────────',
      '',
      (markdown ? '## ' : '') +
        String(index + 1).padStart(2, '0') +
        '. ' +
        (node.title || 'Untitled scene'),
      '',
    )
    if (node.chapter) lines.push(node.chapter, '')
    if (node.id === project.startNodeId) lines.push('[Opening scene]', '')
    if (node.effects?.length)
      lines.push(
        '[On scene entry: ' +
          node.effects
            .map((effect) => describeEffect(effect, variables))
            .join('; ') +
          ']',
        '',
      )
    node.blocks.forEach((block) => {
      if (block.kind === 'dialogue') {
        lines.push((block.speaker || 'CHARACTER').toUpperCase())
        if (block.direction) lines.push('(' + block.direction + ')')
      }
      lines.push(block.text, '')
    })
    if (node.kind === 'ending') lines.push('THE END', '')
    else if (node.choices.length) {
      lines.push('PLAYER CHOICES', '')
      node.choices.forEach((choice, choiceIndex) => {
        lines.push(
          String(choiceIndex + 1) +
            '. ' +
            (choice.label || '[untitled choice]') +
            ' → ' +
            destination(choice.targetId),
        )
        if (choice.conditions?.length)
          lines.push(
            '   When ' +
              choice.conditions
                .map((condition) => describeCondition(condition, variables))
                .join(choice.conditionMode === 'any' ? ' or ' : ' and ') +
              ' · otherwise ' +
              (choice.unavailableMode === 'hide' ? 'hidden' : 'locked'),
          )
        if (choice.effects?.length)
          lines.push(
            '   ' +
              choice.effects
                .map((effect) => describeEffect(effect, variables))
                .join('; '),
          )
      })
      lines.push('')
    } else
      lines.push(
        node.nextId
          ? 'Continue → ' + destination(node.nextId)
          : '[Next scene to be written]',
        '',
      )
  })
  return lines.join('\n') + '\n'
}

export function downloadManuscript(project: StoryProject, markdown = false) {
  const blob = new Blob([manuscriptText(project, markdown)], {
    type: markdown ? 'text/markdown;charset=utf-8' : 'text/plain;charset=utf-8',
  })
  downloadBlob(
    blob,
    slugify(project.title, 'untitled-story') + (markdown ? '.md' : '.txt'),
  )
}
